import { AntDesign } from '@expo/vector-icons';
import * as MailComposer from 'expo-mail-composer';
import { Href, router } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React from 'react';
import { Linking, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import Toast from 'react-native-toast-message';

import { BS_CONTACT_EMAIL, PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@/constants/config';
import { SettingsRoutes } from '@/constants/enums/SettingsRoutesNames';
import { useAuth } from '@/contexts/authContext';
import { useBottomSheet } from '@/contexts/bottomSheetContext';
import { i18n } from '@/utils/i18n';

import DeleteAccountBottomSheet from '../bottomSheets/DeleteAccountBottomSheet';
import LogOutBottomSheet from '../bottomSheets/LogOutBottomSheet';
import Text from '../Text';

export type UserType = 'ADMIN' | 'NON_PROFIT_ORG' | 'INDIVIDUAL' | 'BROWSE_ONLY' | 'ALL';

//Interfaces
export interface OptionItemProps {
  icon: React.JSX.Element;
  label: string;
  screen: SettingsRoutes;
  userType: UserType[];
}

//Components
export const OptionItem = ({ icon, label, screen }: OptionItemProps) => {
  const { state } = useAuth();
  const dispatch = useBottomSheet().dispatch;

  const onPressTermsPrivacy = (href: Href, url: string) => {
    if (Platform.OS === 'web') router.navigate(href);
    else WebBrowser.openBrowserAsync(url);
  };

  const handleTouch = () => {
    switch (screen) {
      case SettingsRoutes.PAYMENT_METHODS:
        router.navigate(`/(app)/${screen}`);
        break;

      case SettingsRoutes.SHOW:
        router.navigate(`/(app)/${screen}`);
        break;

      case SettingsRoutes.MERCHANT_DOCUMENTS:
        router.navigate({
          pathname: '/(app)/npoPaymentOnboarding/documents',
          params: {
            npoId: state.user?.user.nonProfitOrgId
          }
        });
        break;

      case SettingsRoutes.CONTACT_BS:
        // ToDO: Change for the real data
        if (Platform.OS === 'web') {
          Linking.openURL(
            `mailto:${BS_CONTACT_EMAIL}?subject=Support - Broken Shepherds™ # ${state.user?.user?.fullName}&body=Hi my name is ${state.user?.user?.fullName} i need help with...`
          );
        } else {
          try {
            MailComposer.composeAsync({
              recipients: [BS_CONTACT_EMAIL],
              subject: `Support - Broken Shepherds™ # ${state.user?.user?.fullName}`,
              body: `Hi my name is ${state.user?.user?.fullName} i need help with...`
            });
          } catch (e) {
            console.error(e);
            Toast.show({
              type: 'error',
              text1: i18n.t('mix.mail_not_openned.title'),
              text2: i18n.t('mix.mail_not_openned.message'),
              position: 'bottom',
              visibilityTime: 3500
            });
          }
        }
        break;

      case SettingsRoutes.LOG_OUT:
        dispatch({
          type: 'SET_BOTTOMSHEET',
          setHeight: 350,
          setContent: <LogOutBottomSheet />,
          setVisible: true
        });
        break;

      case SettingsRoutes.DELETE_ACCNT:
        dispatch({
          type: 'SET_BOTTOMSHEET',
          setHeight: 450,
          setContent: <DeleteAccountBottomSheet />,
          setVisible: true
        });
        break;

      case SettingsRoutes.PRIVACY_POLICY:
        onPressTermsPrivacy('/privacyPolicy', PRIVACY_POLICY_URL);
        break;

      case SettingsRoutes.TERMS_CONDITIONS:
        onPressTermsPrivacy('/termsOfService', TERMS_OF_USE_URL);
        break;
      default:
        router.navigate(`/profile/settings/${screen}`);
        break;
    }
  };

  return (
    <View>
      <TouchableOpacity onPress={() => handleTouch()} style={style.optionItem}>
        {icon}
        <View style={style.flexOne}>
          <Text style={style.optionLabel} fontWeight="500">
            {label}
          </Text>
        </View>
        <AntDesign name="right" size={24} color={'black'} />
      </TouchableOpacity>
    </View>
  );
};

const style = StyleSheet.create({
  optionItem: {
    paddingVertical: 21,
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 26
  },

  optionLabel: {
    fontSize: 16,
    color: 'black'
  },

  flexOne: {
    flex: 1
  }
});
